/* eslint-disable react-refresh/only-export-components */
import { TextField } from '@mui/material';
import { withJsonFormsControlProps } from '@jsonforms/react';
import {
  and,
  type ControlProps,
  isStringControl,
  optionIs,
  rankWith,
  type JsonFormsRendererRegistryEntry,
} from '@jsonforms/core';

/**
 * Custom TextArea renderer for JSONForms that properly handles placeholder option.
 * This renderer is used for multiline text inputs and respects the placeholder
 * option defined in the ui_schema.
 */
const TextAreaControl = ({
  data,
  handleChange,
  path,
  label,
  visible = true,
  enabled = true,
  errors,
  uischema,
}: ControlProps) => {
  if (!visible) {
    return null;
  }

  // Extract options from uischema
  const options = uischema?.options || {};
  const placeholder = (options.placeholder as string) || undefined;
  const minRows = (options.minRows as number) || 3;
  const maxRows = (options.maxRows as number) || undefined;

  // Check if label should be hidden (when uischema.label is explicitly false)
  const showLabel = uischema?.label !== false;

  return (
    <TextField
      label={showLabel ? label : undefined}
      value={data || ''}
      onChange={(event) => handleChange(path, event.target.value)}
      disabled={!enabled}
      error={errors !== undefined && errors.length > 0}
      helperText={errors}
      multiline
      minRows={minRows}
      maxRows={maxRows}
      placeholder={placeholder}
      fullWidth
      margin="normal"
    />
  );
};

const TextAreaRenderer = withJsonFormsControlProps(TextAreaControl);

/**
 * Tester for the TextArea renderer.
 * Activates when:
 * 1. The control is for a string field
 * 2. The ui_schema has options.multi set to true
 *
 * RENDERER RANKING STRATEGY:
 * JSONForms uses a ranking system to determine which renderer to use.
 * Higher ranks take priority over lower ranks.
 *
 * Current Ranking Hierarchy:
 * - Rank 1-3:   Core JSONForms default renderers (text, number, boolean, etc.)
 * - Rank 4:     Custom control renderers (this TextAreaRenderer)
 * - Rank 5:     Custom layout renderers (GroupLayoutRenderer, EntitySelectRenderer)
 * - Rank 10+:   Reserved for future highly-specific renderers
 *
 * This renderer uses rank 4 to override the default string renderer (rank 1)
 * when the 'multi' option is set, enabling multiline text input.
 */
export const textAreaRendererTester = rankWith(
  4, // Higher priority than default string renderer (which is 1)
  and(isStringControl, optionIs('multi', true)),
);

export const textAreaRendererEntry: JsonFormsRendererRegistryEntry = {
  tester: textAreaRendererTester,
  renderer: TextAreaRenderer,
};
